import { makeAutoObservable } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { createClient } from "@/core/helpers";
import { errorLogger } from "@/core/errors";
import { ApiClient } from "@/clients/ApiClient";

export interface RecentSearchEntry {
    id: string;
    query: string;
    resultCount: number;
    timestamp: number;
}

export interface SavedSearchEntry {
    id: string;
    query: string;
    createdAt: number;
    lastUsedAt: number;
}

const MAX_RECENT_SEARCHES = 10;
const MAX_SAVED_SEARCHES = 20;
const SEARCH_TIMEOUT_MS = 5000;

export const SEARCH_TIMEOUT_MESSAGE = "Sökningen tog för lång tid. Försök igen.";
export const SEARCH_FAILURE_MESSAGE = "Sökningen misslyckades. Försök igen.";
export const SEARCH_OFFLINE_MESSAGE = "Du verkar vara offline. Lokala resultat visas.";

export class SearchStore {
    result = {
        results: [],
    };
    podcasts: Podcast[] = [];
    episodes: Episode[] = [];
    recentSearches: RecentSearchEntry[] = [];
    savedSearches: SavedSearchEntry[] = [];
    searchResult: Episode[] = [];
    lastQuery = "";
    loading = false;
    error: string | null = null;
    private client: ApiClient;
    private cachedSearchQueries: Map<string, { podcasts: Podcast[]; episodes: Episode[] }> = new Map();

    constructor() {
        makeAutoObservable(this);
        makePersistable(this, {
            name: "SearchStore",
            properties: ["recentSearches", "savedSearches"],
            storage: window.localStorage,
            expireIn: 1000 * 60 * 60 * 24 * 30,
        }).catch((error) => {
            console.error("Error persisting SearchStore", error);
        });
        this.client = createClient();
    }

    async getFilteredPodcasts(input: string, page = 1) {
        if (!input.trim()) {
            this.result.results = [];
        } else {
            this.result = await this.client.get(`/podcasts/filter/${input}?limit=4&page=${page}`);
        }
    }

    async searchPodcastsAndEpisodes(query: string) {
        this.podcasts = [];
        this.lastQuery = "";
        const trimmedQuery = query.trim();

        if (trimmedQuery.length >= 2) {
            this.lastQuery = trimmedQuery;
            const { podcasts } = await this.client.get<PodcastsResponse>(
                `/podcasts?q=` + encodeURIComponent(trimmedQuery)
            );
            this.podcasts = podcasts;

            const { episodes } = await this.client.get<EpisodesResponse>(
                `/episodes?q=` + encodeURIComponent(trimmedQuery)
            );
            this.episodes = episodes;
        } else {
            this.episodes = [];
        }
    }

    /**
     * Auto-searches podcasts and episodes if not already cached.
     * Safe to call multiple times - only fetches once per query.
     */
    async ensureSearchResults(query: string) {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
            this.podcasts = [];
            this.episodes = [];
            this.lastQuery = "";
            this.error = null;
            return;
        }

        // Check cache first
        const cached = this.cachedSearchQueries.get(trimmedQuery);
        if (cached) {
            this.podcasts = cached.podcasts;
            this.episodes = cached.episodes;
            this.lastQuery = trimmedQuery;
            this.error = null;
            return;
        }

        this.loading = true;
        this.error = null;
        try {
            const [podcastResponse, episodeResponse] = await Promise.all([
                withSearchTimeout(
                    this.client.get<PodcastsResponse>(`/podcasts?q=${encodeURIComponent(trimmedQuery)}`),
                    SEARCH_TIMEOUT_MS
                ),
                withSearchTimeout(
                    this.client.get<EpisodesResponse>(`/episodes?q=${encodeURIComponent(trimmedQuery)}`),
                    SEARCH_TIMEOUT_MS
                ),
            ]);
            const podcasts = Array.isArray(podcastResponse?.podcasts) ? podcastResponse.podcasts : [];
            const episodes = Array.isArray(episodeResponse?.episodes) ? episodeResponse.episodes : [];

            this.podcasts = podcasts;
            this.episodes = episodes;
            this.lastQuery = trimmedQuery;

            // Cache the results
            this.cachedSearchQueries.set(trimmedQuery, { podcasts, episodes });
        } catch (error) {
            this.podcasts = [];
            this.episodes = [];
            this.lastQuery = trimmedQuery;
            this.error = getSearchErrorMessage(error);

            errorLogger.logError(error instanceof Error ? error : new Error(String(error)), undefined, {
                action: "ensureSearchResults",
                additionalData: {
                    queryLength: trimmedQuery.length,
                },
                component: "SearchStore",
            });
        } finally {
            this.loading = false;
        }
    }

    async searchEpisodesInPodcast(query: string, podcastId: number) {
        if (query.length > 0) {
            const { episodes } = await this.client.get<EpisodesResponse>(
                `/podcasts/${podcastId}/episodes?filter=${encodeURIComponent(query)}`
            );
            this.searchResult = episodes;
        } else {
            this.searchResult = [];
        }
    }

    addRecentSearch(query: string, resultCount = this.resultCount) {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2 || resultCount <= 0) {
            return;
        }

        const existing = this.recentSearches.find(
            (search) => search.query.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase()
        );
        const nextEntry: RecentSearchEntry = {
            id: existing?.id ?? createRecentSearchId(trimmedQuery),
            query: trimmedQuery,
            resultCount,
            timestamp: Date.now(),
        };

        this.recentSearches = [nextEntry, ...this.recentSearches.filter((search) => search.id !== nextEntry.id)].slice(
            0,
            MAX_RECENT_SEARCHES
        );
    }

    removeRecentSearch(id: string) {
        this.recentSearches = this.recentSearches.filter((search) => search.id !== id);
    }

    clearRecentSearches() {
        this.recentSearches = [];
    }

    saveSearch(query: string) {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
            return;
        }

        const existing = this.savedSearches.find(
            (search) => search.query.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase()
        );
        const nextEntry: SavedSearchEntry = {
            id: existing?.id ?? createSavedSearchId(trimmedQuery),
            query: trimmedQuery,
            createdAt: existing?.createdAt ?? Date.now(),
            lastUsedAt: Date.now(),
        };

        this.savedSearches = [nextEntry, ...this.savedSearches.filter((search) => search.id !== nextEntry.id)].slice(
            0,
            MAX_SAVED_SEARCHES
        );
    }

    markSavedSearchUsed(id: string) {
        const existing = this.savedSearches.find((search) => search.id === id);
        if (!existing) {
            return;
        }

        existing.lastUsedAt = Date.now();
    }

    removeSavedSearch(id: string) {
        this.savedSearches = this.savedSearches.filter((search) => search.id !== id);
    }

    get resultCount() {
        return this.podcasts.length + this.episodes.length;
    }
}

async function withSearchTimeout<T>(promise: Promise<T>, timeoutMs: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeout = setTimeout(() => {
                    reject(new Error("SearchTimeout"));
                }, timeoutMs);
            }),
        ]);
    } finally {
        if (timeout) {
            clearTimeout(timeout);
        }
    }
}

function getSearchErrorMessage(error: unknown) {
    if (error instanceof Error && error.message === "SearchTimeout") {
        return SEARCH_TIMEOUT_MESSAGE;
    }

    if (typeof navigator !== "undefined" && "onLine" in navigator && navigator.onLine === false) {
        return SEARCH_OFFLINE_MESSAGE;
    }

    return SEARCH_FAILURE_MESSAGE;
}

function createRecentSearchId(query: string) {
    const normalizedQuery = query.toLocaleLowerCase().replace(/\s+/g, "-");
    return `recent-${normalizedQuery}`;
}

function createSavedSearchId(query: string) {
    const normalizedQuery = query.toLocaleLowerCase().replace(/\s+/g, "-");
    return `saved-${normalizedQuery}`;
}

export function formatRecentSearchTimestamp(timestamp: number) {
    const diff = Date.now() - timestamp;

    if (diff < 60_000) {
        return "Just nu";
    }

    if (diff < 3_600_000) {
        return `${Math.floor(diff / 60_000)} min sedan`;
    }

    if (diff < 86_400_000) {
        return `${Math.floor(diff / 3_600_000)} tim sedan`;
    }

    return new Date(timestamp).toLocaleDateString("sv-SE");
}
